import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;
const DRAFT_KEY = "telefonka-booking-draft-v5";

const FALLBACK_PUBLIC_SPECS = {
  audio: { name: "Fehér audió Telefonka", price: 39000 },
  video: { name: "Videós Telefonka", price: 119000 },
};

const LEGACY_PUBLIC_NAMES = new Set([
  "black retro phone",
  "red retro phone",
  "white retro phone",
]);

export const normalizeDeviceId = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

const normalizeText = (value) => String(value || "").trim().toLocaleLowerCase();

export const normalizeBookingDraft = (storage = globalThis.localStorage) => {
  if (!storage) return;
  try {
    const raw = storage.getItem(DRAFT_KEY);
    if (!raw) return;
    const draft = JSON.parse(raw);
    if (!draft || !draft.selectedDeviceId) return;
    const normalized = normalizeDeviceId(draft.selectedDeviceId);
    if (normalized !== draft.selectedDeviceId) {
      storage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, selectedDeviceId: normalized }));
    }
  } catch (_) {
    // Private browsing / invalid legacy draft must never block booking startup.
  }
};

export const createCatalogIndex = (catalog) => {
  const devices = Array.isArray(catalog?.devices) ? catalog.devices : [];
  return new Map(
    devices.map((device) => [normalizeDeviceId(device.id), { ...device, id: normalizeDeviceId(device.id) }])
  );
};

export const fallbackPublicDevices = (devices) => {
  if (!Array.isArray(devices)) return [];

  const byType = new Map();
  devices.forEach((device) => {
    const type = normalizeText(device?.type);
    const spec = FALLBACK_PUBLIC_SPECS[type];
    const id = normalizeDeviceId(device?.id);
    const name = normalizeText(device?.name);

    if (!spec || !id || LEGACY_PUBLIC_NAMES.has(name) || byType.has(type)) return;

    byType.set(type, {
      ...device,
      id,
      type,
      name: spec.name,
      price: spec.price,
    });
  });

  return ["audio", "video"].map((type) => byType.get(type)).filter(Boolean);
};

export const adaptPublicDevices = (devices, catalogIndex) => {
  if (!Array.isArray(devices)) return [];

  if (!(catalogIndex instanceof Map) || catalogIndex.size === 0) {
    return fallbackPublicDevices(devices);
  }

  return devices
    .map((device) => {
      const id = normalizeDeviceId(device?.id);
      const canonical = catalogIndex.get(id);
      if (!canonical) return null;
      return {
        ...device,
        ...canonical,
        id,
        price: Number(canonical.price),
      };
    })
    .filter(Boolean);
};

export const adaptAvailability = (payload, catalogIndex) => {
  const data = payload && typeof payload === "object" ? payload : {};
  return {
    ...data,
    available_devices: adaptPublicDevices(data.available_devices || [], catalogIndex),
    unavailable_device_ids: Array.isArray(data.unavailable_device_ids)
      ? data.unavailable_device_ids.map(normalizeDeviceId)
      : [],
  };
};

const pathname = (config) => {
  try {
    const base = config?.baseURL || globalThis.location?.origin || "http://localhost";
    return new URL(config?.url || "", base).pathname.replace(/\/$/, "");
  } catch (_) {
    return String(config?.url || "").split("?")[0].replace(/\/$/, "");
  }
};

export const installBookingCatalogAdapter = async () => {
  normalizeBookingDraft();

  // Install the public-device guard before loading the catalogue. This matters
  // during rolling frontend/backend deploys: even if /booking-catalog is briefly
  // unavailable, legacy Black/Red/White records must never leak back into the
  // public booking selector.
  let catalogIndex = new Map();

  const responseInterceptor = axios.interceptors.response.use((response) => {
    const path = pathname(response.config);
    if (path.endsWith("/api/devices")) {
      response.data = adaptPublicDevices(response.data, catalogIndex);
    } else if (path.endsWith("/api/availability")) {
      response.data = adaptAvailability(response.data, catalogIndex);
    }
    return response;
  });

  const requestInterceptor = axios.interceptors.request.use((config) => {
    const path = pathname(config);
    if (path.endsWith("/api/bookings") && config.data) {
      let payload = config.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (_) {
          return config;
        }
      }
      if (payload && typeof payload === "object" && payload.device_id !== undefined) {
        config.data = { ...payload, device_id: normalizeDeviceId(payload.device_id) };
      }
    }
    return config;
  });

  try {
    const response = await axios.get(`${API}/booking-catalog`);
    const loadedIndex = createCatalogIndex(response.data);
    if (loadedIndex.size === 2) catalogIndex = loadedIndex;
  } catch (_) {
    // The guard stays active with a strict type-based fallback. Payment remains
    // server-authoritative, and legacy named devices are still excluded.
  }

  return () => {
    axios.interceptors.response.eject(responseInterceptor);
    axios.interceptors.request.eject(requestInterceptor);
  };
};
